import AOS from 'aos';
import 'aos/dist/aos.css';
import './tracking.js';

// Alpine.js — bundled here (~15 KB gzipped) so the navbar, reservation
// seats spinner, reviews widget, menu sidebar, and FAQ accordion work on
// every page. Previously Alpine came in as a side-effect of
// @livewireScripts; we scoped that to /blog only to save ~30 KB on the
// other pages, so Alpine now ships separately.
//
// Livewire 3 on /blog imports its own Alpine under window.Alpine —
// because it loads AFTER this bundle, our Alpine.start() below runs
// first, and Livewire's hydration layer detects the already-initialised
// instance and cooperates instead of double-starting.
import Alpine from 'alpinejs';
window.Alpine = Alpine;
Alpine.start();

AOS.init({
    duration: 800,
    easing: 'ease-out-cubic',
    once: true,
    offset: 60,
});

/* ──────────────────────────────────────────────────────────────
   Text reveal — word/char stagger driven by IntersectionObserver
   and spring-ish CSS transitions.

   Usage in Blade:
     <h1 data-reveal>Hello <span class="italic">world</span></h1>
     <h2 data-reveal="chars">Selamat Datang</h2>

   Pre-existing inline tags are preserved. Whitespace is kept so
   justified text doesn't collapse.
   ────────────────────────────────────────────────────────────── */

function splitWords(el) {
    if (el.dataset.revealReady === '1') return;

    const nodes = Array.from(el.childNodes);
    const out = [];
    let i = 0;

    const wrapWord = (text) => {
        const span = document.createElement('span');
        span.className = 'reveal-word';
        span.style.setProperty('--i', i++);
        span.textContent = text;
        return span;
    };

    const wrapElement = (node) => {
        // Walk inside to preserve styling but still split words.
        const clone = node.cloneNode(false); // shallow
        node.childNodes.forEach((child) => {
            if (child.nodeType === Node.TEXT_NODE) {
                const parts = child.textContent.split(/(\s+)/);
                parts.forEach((p) => {
                    if (!p) return;
                    if (/\s+/.test(p)) {
                        clone.appendChild(document.createTextNode(' '));
                    } else {
                        clone.appendChild(wrapWord(p));
                    }
                });
            } else if (child.nodeType === Node.ELEMENT_NODE) {
                clone.appendChild(wrapElement(child));
            } else {
                clone.appendChild(child.cloneNode(true));
            }
        });
        return clone;
    };

    nodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            const parts = node.textContent.split(/(\s+)/);
            parts.forEach((p) => {
                if (!p) return;
                if (/\s+/.test(p)) {
                    out.push(document.createTextNode(' '));
                } else {
                    out.push(wrapWord(p));
                }
            });
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            // Skip <br>: keep as line break.
            if (node.tagName === 'BR') {
                out.push(node.cloneNode(true));
            } else {
                out.push(wrapElement(node));
            }
        } else {
            out.push(node.cloneNode(true));
        }
    });

    el.innerHTML = '';
    out.forEach((n) => el.appendChild(n));
    el.dataset.revealReady = '1';
}

function splitChars(el) {
    if (el.dataset.revealReady === '1') return;

    const text = el.textContent;
    const frag = document.createDocumentFragment();
    let i = 0;
    for (const ch of text) {
        if (ch === ' ') {
            frag.appendChild(document.createTextNode(' '));
        } else {
            const span = document.createElement('span');
            span.className = 'reveal-char';
            span.style.setProperty('--i', i++);
            span.textContent = ch;
            frag.appendChild(span);
        }
    }
    el.innerHTML = '';
    el.appendChild(frag);
    el.dataset.revealReady = '1';
}

function initTextReveal() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Split word/char targets
    document.querySelectorAll('[data-reveal]').forEach((el) => {
        const mode = el.getAttribute('data-reveal');
        if (mode === 'chars') splitChars(el);
        else splitWords(el);
    });

    // Block targets (paragraphs, glass cards) don't need splitting, just observation.
    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('in-view');
                    observer.unobserve(entry.target);
                }
            });
        },
        { threshold: 0.25, rootMargin: '0px 0px -10% 0px' }
    );

    document
        .querySelectorAll('[data-reveal], [data-reveal-block], [data-reveal-glass]')
        .forEach((el) => {
            if (reduceMotion) {
                el.classList.add('in-view');
            } else {
                observer.observe(el);
            }
        });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTextReveal);
} else {
    initTextReveal();
}

/* Re-run on Livewire updates so article-list etc get the treatment */
document.addEventListener('livewire:initialized', () => {
    Livewire.hook('morph.updated', () => initTextReveal());
});
